import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Divider,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  FormGroup,
  Button,
  Alert,
  IconButton,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { WgApplicationsEnum } from '../../utils/WgApplicationsEnum';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { registerUserAccessAsNewCustomer } from './create-account.api';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LanguageSelector from '../../components/LanguageSelector/LanguageSelector';
import { getCountryList } from '../../utils/getCountryList';
import 'flag-icons/css/flag-icons.min.css';

type CountryOption = {
  code: string;
  className: string;
};

const COUNTRY_OPTIONS: CountryOption[] = [
  { name: 'Argentina', code: 'AR', className: 'fi fi-ar' } as any,
  { name: 'Australia', code: 'AU', className: 'fi fi-au' } as any,
  { name: 'Brazil', code: 'BR', className: 'fi fi-br' } as any,
  { name: 'Colombia', code: 'CO', className: 'fi fi-co' } as any,
  { name: 'China', code: 'CN', className: 'fi fi-cn' } as any,
  { name: 'Chile', code: 'CL', className: 'fi fi-cl' } as any,
  { name: 'El Salvador', code: 'SV', className: 'fi fi-sv' } as any,
  { name: 'Estonia', code: 'EE', className: 'fi fi-ee' } as any,
  { name: 'France', code: 'FR', className: 'fi fi-fr' } as any,
  { name: 'Georgia', code: 'GE', className: 'fi fi-ge' } as any,
  { name: 'Hong Kong', code: 'HK', className: 'fi fi-hk' } as any,
  { name: 'India', code: 'IN', className: 'fi fi-in' } as any,
  { name: 'Japan', code: 'JP', className: 'fi fi-jp' } as any,
  { name: 'New Zealand', code: 'NZ', className: 'fi fi-nz' } as any,
  { name: 'Mexico', code: 'MX', className: 'fi fi-mx' } as any,
  { name: 'Portugal', code: 'PT', className: 'fi fi-pt' } as any,
  { name: 'Paraguay', code: 'PY', className: 'fi fi-py' } as any,
  { name: 'Peru', code: 'PE', className: 'fi fi-pe' } as any,
  { name: 'Poland', code: 'PL', className: 'fi fi-pl' } as any,
  { name: 'Romania', code: 'RO', className: 'fi fi-ro' } as any,
  { name: 'South Africa', code: 'ZA', className: 'fi fi-za' } as any,
  { name: 'Spain', code: 'ES', className: 'fi fi-es' } as any,
  { name: 'Switzerland', code: 'CH', className: 'fi fi-ch' } as any,
  { name: 'Thailand', code: 'TH', className: 'fi fi-th' } as any,
  { name: 'United States', code: 'US', className: 'fi fi-us' } as any
];

const CreateAccount: React.FC = () => {
  const { t, i18n } = useTranslation();
  // Area 1 - Company/Customer
  const [fantasyName, setFantasyName] = useState('');
  const [registrationId, setRegistrationId] = useState('');
  const [responsableName, setResponsableName] = useState('');
  const [responsableEmail, setResponsableEmail] = useState('');
  const normalizeLanguage = (lang: string | undefined | null) => {
    const lng = (lang ?? '').trim();
    if (!lng) return 'en-US';
    if (lng.toLowerCase().startsWith('pt')) return 'pt-BR';
    return 'en-US';
  };

  const [locale, setLocale] = useState<string>(() => normalizeLanguage(i18n.resolvedLanguage ?? i18n.language));
  const [countryCustomer, setCountryCustomer] = useState<string>('');

  // Area 2 - User access
  const [sameAsResponsible, setSameAsResponsible] = useState<'yes' | 'no'>('yes');
  const [userName, setUserName] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [passwordRaw, setPasswordRaw] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Applications selection
  const [apps, setApps] = useState({
    immigrationNotificator: true,
    concursosNotificador: false,
    hrtech: false
  });

  const [apiErrors, setApiErrors] = useState<string[]>([]);
  const navigate = useNavigate();
  const [countryCode, setCountryCode] = useState('');

  const countryList = getCountryList(t);

  // Clear possible browser-remembered values on mount to avoid pre-population
  useEffect(() => {
    setResponsableEmail('');
    setEmailAddress('');
    setPasswordRaw('');
    const timer = setTimeout(() => {
      setResponsableEmail('');
      setEmailAddress('');
      setPasswordRaw('');
    }, 120);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setLocale(normalizeLanguage(i18n.resolvedLanguage ?? i18n.language));
  }, [i18n.language, i18n.resolvedLanguage]);

  useEffect(() => {
    if (sameAsResponsible === 'yes') {
      setUserName(responsableName);
      setEmailAddress(responsableEmail);
    }
  }, [sameAsResponsible, responsableName, responsableEmail]);

  const formatCNPJ = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 14);
    const p0 = digits.slice(0, 2);
    const p1 = digits.slice(2, 5);
    const p2 = digits.slice(5, 8);
    const p3 = digits.slice(8, 12);
    const p4 = digits.slice(12, 14);
    let out = p0;
    if (digits.length > 2) out += '.' + p1;
    if (digits.length > 5) out += '.' + p2;
    if (digits.length > 8) out += '/' + p3;
    if (digits.length > 12) out += '-' + p4;
    return out;
  };

  const handleLocaleChange = (value: string) => {
    setLocale(normalizeLanguage(value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiErrors([]);

    // Simple validations according to constraints
    if (!responsableName || !responsableEmail || !userName || !emailAddress || !passwordRaw || !fantasyName || !countryCode) {
      setApiErrors([t('create_account_error_required_fields')]);
      return;
    }

    // Build apps as enum codes, send only those selected (true)
    const selectedAppCodes: WgApplicationsEnum[] = [];
    if (apps.immigrationNotificator) selectedAppCodes.push(WgApplicationsEnum.IMMIGRATION_CANADA);
    if (apps.concursosNotificador) selectedAppCodes.push(WgApplicationsEnum.CONCURSEIROS_BRASIL);
    if (apps.hrtech) selectedAppCodes.push(WgApplicationsEnum.HR_TECH);

    const payload = {
      userName: userName.trim(),
      emailAddress: emailAddress.trim(),
      password: passwordRaw.trim(),
      responsableName: responsableName.trim(),
      responsableEmail: responsableEmail.trim(),
      applications: selectedAppCodes,
      fantasyName: fantasyName.trim(),
      registrationId: registrationId.replace(/\D/g, '') || undefined,
      language: i18n.language,
      countryCode: countryCode,
      userAccessLocale: i18n.language,
      registrationName: fantasyName.trim(),
      customerCountry: countryCode,
      locale,
      countryCustomer: countryCustomer || undefined
    };

    try {
      const res = await registerUserAccessAsNewCustomer(payload);
      if (res?.success && typeof res?.data === 'string' && res.data) {
        // Navigate to token confirmation page with Guid in URL (also carry selected apps in state)
        navigate(`/confirmacao-token?guid=${encodeURIComponent(res.data)}`, { state: { selectedApps: selectedAppCodes } });
      } else {
        setApiErrors([res?.message || t('create_account_error_register_failed')]);
      }
    } catch (error) {
      const respData = (error as any)?.response?.data;
      const collected: string[] = [];

      if (respData) {
        if (Array.isArray(respData.errors)) {
          for (const item of respData.errors) {
            const msg = (item && (item.errorMessage || item.message)) as string | undefined;
            if (msg) {
              collected.push(msg);
            }
          }
        }
        if (respData.message && typeof respData.message === 'string') {
          collected.push(respData.message);
        }
      }

      setApiErrors(collected.length ? collected : [t('create_account_error_generic')]);
      console.error('Error registering new customer:', error);
    }
  };

  return (
    <Container component="main" maxWidth="md">
      <Box sx={{ mt: 6 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h4" component="h1" color="primary" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonAddIcon fontSize="large" sx={{ mr: 1 }} />
              {t('create_account_title')}
            </Typography>
            <LanguageSelector onChanged={(lang) => handleLocaleChange(lang)} />
          </Box>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t('create_account_description')}
          </Typography>

          {apiErrors.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {apiErrors.map((msg, idx) => (
                <Alert key={idx} severity="error" sx={{ mb: 1 }}>{msg}</Alert>
              ))}
            </Box>
          )}

          <form onSubmit={handleSubmit} autoComplete="off">
            {/* Autofill decoys to prevent browser pre-populating email/password */}
            <input type="text" name="email" autoComplete="username" style={{ display: 'none' }} aria-hidden="true" tabIndex={-1} />
            <input type="password" name="password" autoComplete="new-password" style={{ display: 'none' }} aria-hidden="true" tabIndex={-1} />
            {/* Area 1: Company/Customer */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              {t('create_account_company_title')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label={t('create_account_label_fantasy_name')}
                  value={fantasyName}
                  onChange={(e) => setFantasyName(e.target.value)}
                  inputProps={{ maxLength: 80 }}
                  helperText={t('common_helper_max_chars', { count: 80 })}
                />
              </Grid>
              <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('create_account_label_registration_id')}
                  value={registrationId}
                  onChange={(e) => setRegistrationId(formatCNPJ(e.target.value))}
                  placeholder={t('customer_mgmt_placeholder_cnpj')}
                  helperText={t('create_account_helper_registration_id_format', { format: t('customer_mgmt_placeholder_cnpj') })}
                  inputProps={{ maxLength: 80, inputMode: 'numeric' }}
                />
              </Grid>
              <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel id="country-select-label" shrink required>{t('common_label_country')}</InputLabel>
                  <Select
                    labelId="country-select-label"
                    value={countryCode}
                    label={t('common_label_country')}
                    onChange={(e) => setCountryCode(e.target.value)}
                    displayEmpty
                    notched
                    required
                    renderValue={(val) => {
                      if (!val) {
                        return (
                          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                            {t('create_account_placeholder_country')}
                          </Typography>
                        );
                      }
                      const c = countryList.find(x => x.code === val);
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span className={c?.className} />
                          <Typography variant="body1">{c?.name}</Typography>
                        </Box>
                      );
                    }}
                  >
                    <MenuItem value="">
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        {t('create_account_placeholder_country')}
                      </Typography>
                    </MenuItem>
                    {countryList.map((c) => (
                      <MenuItem key={c.code} value={c.code}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span className={c.className} />
                          <Typography variant="body1">{c.name}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid component={'div' as React.ElementType} item xs={12}>
                <TextField
                  required
                  fullWidth
                  label={t('create_account_label_responsible_name')}
                  value={responsableName}
                  onChange={(e) => setResponsableName(e.target.value)}
                  inputProps={{ maxLength: 80 }}
                  helperText={t('common_helper_max_chars', { count: 80 })}
                />
              </Grid>
              <Grid component={'div' as React.ElementType} item xs={12}>
                <TextField
                  required
                  fullWidth
                  type="email"
                  label={t('create_account_label_responsible_email')}
                  value={responsableEmail}
                  onChange={(e) => setResponsableEmail(e.target.value)}
                  autoComplete="off"
                  name="wg-create-responsible-email"
                  inputProps={{ maxLength: 80, autoComplete: 'off' }}
                  helperText={t('common_helper_max_chars', { count: 80 })}
                />
              </Grid>
              <Grid component={'div' as React.ElementType} item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="country-origin-label">{t('create_account_label_country_origin')}</InputLabel>
                  <Select
                    labelId="country-origin-label"
                    label={t('create_account_label_country_origin')}
                    displayEmpty
                    value={countryCustomer}
                    onChange={(e) => setCountryCustomer(e.target.value as string)}
                    sx={{ width: '100%' }}
                    renderValue={(selected) => {
                      if (!selected) return t('create_account_label_country_origin');
                      const found = COUNTRY_OPTIONS.find(c => c.code === selected);
                      if (!found) return selected;
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <span className={found.className} style={{ marginRight: 8 }} />
                          {t(`country_${found.code}`)}
                        </Box>
                      );
                    }}
                  >
                    <MenuItem value="">
                      <em>{t('create_account_label_country_origin')}</em>
                    </MenuItem>
                    {COUNTRY_OPTIONS.map((opt) => (
                      <MenuItem key={opt.code} value={opt.code}>
                        <span className={opt.className} style={{ marginRight: 8 }} />
                        {t(`country_${opt.code}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Area 2: User Access */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 4, mb: 1 }}>
              {t('create_account_user_access_title')}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <RadioGroup
              row
              value={sameAsResponsible}
              onChange={(e) => setSameAsResponsible(e.target.value as 'yes' | 'no')}
            >
              <FormControlLabel value="yes" control={<Radio />} label={t('create_account_radio_use_responsible')} />
              <FormControlLabel value="no" control={<Radio />} label={t('create_account_radio_define_different')} />
            </RadioGroup>

            <Grid container spacing={2}>
              <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label={t('create_account_label_user_name')}
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  inputProps={{ maxLength: 80 }}
                  helperText={t('common_helper_max_chars', { count: 80 })}
                  disabled={sameAsResponsible === 'yes'}
                />
              </Grid>
              <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  type="email"
                  label={t('login_label_email')}
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  autoComplete="off"
                  name="wg-create-email"
                  inputProps={{ maxLength: 80, autoComplete: 'off' }}
                  helperText={t('common_helper_max_chars', { count: 80 })}
                  disabled={sameAsResponsible === 'yes'}
                />
              </Grid>
              <Grid component={'div' as React.ElementType} item xs={12} md={6}>
              <TextField
                required
                fullWidth
                type={showPassword ? 'text' : 'password'}
                label={t('login_label_password')}
                value={passwordRaw}
                onChange={(e) => setPasswordRaw(e.target.value)}
                autoComplete="new-password"
                name="wg-create-password"
                inputProps={{ maxLength: 15, autoComplete: 'new-password' }}
                helperText={t('common_helper_max_chars', { count: 15 })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? t('login_aria_hide_password') : t('login_aria_show_password')}
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              </Grid>
            </Grid>

            {/* Applications selection */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 4, mb: 1 }}>
              {t('create_account_apps_title')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <FormGroup>
              <FormControlLabel
                control={<Checkbox checked={apps.immigrationNotificator} onChange={(e) => setApps(a => ({ ...a, immigrationNotificator: e.target.checked }))} />}
                label={t('create_account_apps_immigration_label')}
              />
             {/*  <FormControlLabel
                control={<Checkbox checked={apps.concursosNotificador} onChange={(e) => setApps(a => ({ ...a, concursosNotificador: e.target.checked }))} disabled />}
                label="Notificador para Concurseiros (em breve)"
              />*/}
              <FormControlLabel
                control={<Checkbox checked={apps.hrtech} onChange={(e) => setApps(a => ({ ...a, hrtech: e.target.checked }))} disabled />}
                label={t('create_account_apps_hrtech_label')}
              />
            </FormGroup>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/login')}>{t('common_btn_back')}</Button>
              <Button type="submit" variant="contained" startIcon={<CheckCircleIcon />}>{t('common_btn_confirm')}</Button>
            </Box>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default CreateAccount;
